/**
 * EvoHome Improvements — Guided Onboarding registry.
 *
 * Reusable, data-driven tour definitions. Additional roles (consultant,
 * introducer, surveyor, customer service, sales, marketing, admin) can be
 * added later by appending an entry to `TOURS` — no framework changes needed.
 *
 * IMPORTANT: every step must describe functionality that genuinely exists.
 */

export type OnboardingAudience = "homeowner" | "installer";

export interface OnboardingStep {
  id: string;
  title: string;
  /** Short paragraphs. Kept deliberately brief — progressive disclosure. */
  body: string[];
  /** Optional bullet list of concrete things the user can do. */
  points?: string[];
  /** Optional in-app destination this step refers to. */
  route?: string;
  routeLabel?: string;
  /** Optional related help article slug. */
  helpSlug?: string;
  /** Optional DOM anchor (data-tour attribute value) this step points at. */
  anchor?: string;
}

export interface OnboardingTour {
  audience: OnboardingAudience;
  /** Bump when the tour content changes materially so it is offered again. */
  version: number;
  title: string;
  intro: string;
  helpRoute: string;
  steps: OnboardingStep[];
}

const HOMEOWNER_TOUR: OnboardingTour = {
  audience: "homeowner",
  version: 3,
  title: "Welcome to EvoHome Improvements",
  intro:
    "Your Home Hub is useful from the day you join, whether the work is next month or a few years away. It holds your property information, the improvements you are considering, any live project, proposals, documents and messages. This short introduction takes a couple of minutes, and you may skip or reopen it at any time.",

  helpRoute: "/customer/help",
  steps: [
    {
      id: "welcome",
      title: "What your Home Hub is for",
      body: [
        "EvoHome Improvements is a home improvement consultancy. We help you scope the work properly, introduce a suitable vetted installer and stay alongside you until the work is finished.",
        "You do not need to be ready to buy. Many members join while they are still considering improvements for next year or the year after, and use their Home Hub in the meantime.",
      ],
      route: "/customer",
      routeLabel: "Open your Home Hub",
    },
    {
      id: "not-ready",
      title: "Joining before you are ready",
      body: [
        "There is no obligation and no pressure. Recording what you are considering simply means our guidance is relevant when the time comes.",
        "Nothing is shared with an installer until you ask us to arrange quotations.",
      ],
      points: [
        "Record improvements you are considering, even years ahead",
        "Set a rough month and year so we do not contact you too early",
        "Update, defer or remove a plan whenever your thinking changes",
      ],
      route: "/customer",
      routeLabel: "See What I am planning",
    },
    {
      id: "home-record",
      title: "Your Home Record",
      body: [
        "Your Home Record is what EvoHome Improvements holds about your property: address, property type, energy information and details confirmed over time.",
        "It is built up gradually. We ask you to confirm what we already hold rather than asking for the same information again, and every change keeps its history.",
      ],
      route: "/customer/property",
      routeLabel: "Open your Home Record",
      helpSlug: "homeowner-property",
    },
    {
      id: "account",
      title: "Your account and contact details",
      body: [
        "Your Account page holds your name, contact details and communication preferences. Keeping these accurate means appointment confirmations and project updates reach you.",
      ],
      points: [
        "Check your name, email address and mobile number",
        "Set how you prefer to be contacted",
        "Change your password from your account settings",
      ],
      route: "/customer/account",
      routeLabel: "Open Account",
      helpSlug: "homeowner-account",
    },

    {
      id: "project",
      title: "Your enquiry and project",
      body: [
        "Once your enquiry is under way it appears on your Home Hub as a project. Opening it shows the current stage, what happens next and any appointments arranged with you.",
        "Statuses are written in plain language, for example work scheduled, work in progress or awaiting sign-off.",
      ],
      route: "/customer",
      routeLabel: "See your projects",
      helpSlug: "homeowner-project-status",
    },
    {
      id: "proposals",
      title: "Proposals and quotations",
      body: [
        "When a proposal has been prepared and approved for you, it appears within your project. It sets out the recommended solution, what is included, the price and what happens next.",
        "Take your time reading it. Your consultant will talk it through with you and answer questions before any decision is made.",
      ],
      route: "/customer",
      routeLabel: "Open your project",
      helpSlug: "homeowner-proposals",
    },
    {
      id: "installer",
      title: "Your installer confidence profile",
      body: [
        "EvoHome Improvements does not publish a public installer directory. When an installer has been selected for your project, a confidence profile appears within that project.",
        "It shows who they are, their specialisms, verification status and workmanship guarantee. Direct contact details are not shown, because coordination stays with EvoHome Improvements.",
      ],
      helpSlug: "homeowner-installer-profile",
    },
    {
      id: "messages",
      title: "Messages",
      body: [
        "Your Messages page is where communication with your EvoHome Improvements team is kept. Keeping it here means nothing is lost in a personal inbox and everyone is working from the same history.",
      ],
      route: "/customer/messages",
      routeLabel: "Open Messages",
      helpSlug: "homeowner-messages",
    },
    {
      id: "documents",
      title: "Documents",
      body: [
        "Documents belonging to your project — proposals, contracts, survey reports where applicable and completion paperwork — are held in the Documents area of your project.",
      ],
      helpSlug: "homeowner-documents",
    },
    {
      id: "completion",
      title: "Completion, aftercare and reviews",
      body: [
        "When work is finished, your project moves to completion and the aftercare area becomes the place to raise anything that needs attention.",
        "You may also be invited to leave a review. Reviews are always optional.",
      ],
      helpSlug: "homeowner-aftercare",
    },
    {
      id: "support",
      title: "Raising an issue or asking for help",
      body: [
        "Support is where you raise a question, a concern or a complaint. Each request is tracked so you can see that it has been received and what is happening with it.",
        "Anything you raise stays attached to your home record, so nothing has to be explained twice.",
      ],
      route: "/customer/support",
      routeLabel: "Open Support",
    },
    {

      id: "help",
      title: "Where to get help",
      body: [
        "Help and Tutorials is always available from your Home Hub navigation. You may reopen this introduction from there at any time.",
        "If you need a person, email office@evohomeimprovements.co.uk and a member of the team will help.",
      ],
      route: "/customer/help",
      routeLabel: "Open Help and Tutorials",
    },
  ],
};

const INSTALLER_TOUR: OnboardingTour = {
  audience: "installer",
  version: 2,
  title: "Welcome to the EvoHome Improvements Installer Network",
  intro:
    "Your installer workspace is where you complete your business profile, maintain compliance evidence, quote for work, run assigned jobs and submit invoices. This guide walks through setting up correctly. You may skip or reopen it at any time.",
  helpRoute: "/portal/installer/help",
  steps: [
    {
      id: "welcome",
      title: "What the installer workspace does",
      body: [
        "EvoHome Improvements scopes work with the homeowner, then invites suitable installers to quote. Work is allocated by EvoHome Improvements — there is no open marketplace and no unrestricted access to homeowners.",
        "Your workspace covers your profile, compliance, quotes, assigned jobs, evidence and invoicing.",
      ],
      route: "/portal/installer",
      routeLabel: "Open your workspace",
    },
    {
      id: "business-profile",
      title: "Your business profile",
      body: [
        "Start with your business details: company and trading name, business contact details, trades, service categories and years of experience.",
        "This information determines which opportunities are appropriate for you, so it is worth completing carefully.",
      ],
      route: "/portal/installer/profile",
      routeLabel: "Open Profile",
      helpSlug: "installer-profile",
    },
    {
      id: "logo",
      title: "Your company logo",
      body: [
        "Upload your company logo on your profile. It is used on documents your business issues through the platform, such as your invoices.",
        "Your logo is never replaced with the EvoHome Improvements logo on your own documents.",
      ],
      route: "/portal/installer/profile",
      routeLabel: "Upload your logo",
      helpSlug: "installer-logo",
    },
    {
      id: "confidence",
      title: "Your homeowner confidence profile",
      body: [
        "Some profile information may be shown to a homeowner once you are involved in their project or quotation. This is not a public directory listing.",
        "Where supported, that may include your company bio, imagery, portfolio, specialisms, accreditations and workmanship guarantee. Each of these is only shown after EvoHome Improvements has approved it.",
        "Private information — your business contact details, rates, commercial terms and internal assessment — is never shown to homeowners.",
      ],
      route: "/portal/installer/profile",
      routeLabel: "Complete your profile",
      helpSlug: "installer-confidence-profile",
    },
    {
      id: "compliance",
      title: "Compliance evidence",
      body: [
        "Your Compliance page lists the documents required for the trades you work in, such as insurance and relevant accreditations, together with reference numbers and expiry dates.",
        "Documents are reviewed by EvoHome Improvements. Expired or missing evidence can prevent work being allocated to you, so keep expiry dates current.",
      ],
      route: "/portal/installer/compliance",
      routeLabel: "Open Compliance",
      helpSlug: "installer-compliance",
    },
    {
      id: "capabilities",
      title: "Services, capabilities and coverage",
      body: [
        "Your trades, service categories and the areas you cover are held on your profile. They are used when EvoHome Improvements decides which installers to invite to quote.",
        "Availability may also be set so that scheduling reflects when your team can work.",
      ],
      route: "/portal/installer/availability",
      routeLabel: "Set availability",
      helpSlug: "installer-capabilities",
    },
    {
      id: "opportunities",
      title: "Opportunities and assigned jobs",
      body: [
        "Quote requests appear in your Quotes inbox. Jobs allocated to you appear on your Jobs list.",
        "You see the scope, property area and information needed to price and deliver the work. You do not see other installers' pricing, and homeowner contact details remain with EvoHome Improvements.",
      ],
      route: "/portal/installer/quotes",
      routeLabel: "Open Quotes",
      helpSlug: "installer-opportunities",
    },
    {
      id: "quoting",
      title: "Preparing and submitting a quote",
      body: [
        "Open a quote request, review the scope, then enter your pricing and any assumptions or exclusions before submitting.",
        "After submission, EvoHome Improvements reviews the quote and builds the homeowner proposal. You will be notified if you are selected.",
      ],
      route: "/portal/installer/quotes",
      routeLabel: "Open a quote request",
      helpSlug: "installer-quoting",
    },
    {
      id: "communication",
      title: "Project communication",
      body: [
        "Project messages live on the job itself, so everything stays attached to the correct project rather than a personal inbox.",
        "Communication with the homeowner is coordinated by EvoHome Improvements. Message us on the job and we will handle homeowner contact.",
      ],
      helpSlug: "installer-communication",
    },
    {
      id: "evidence",
      title: "During the job: updates, photos and evidence",
      body: [
        "Each job has an evidence upload area for progress photos and required documentation.",
        "Photographic evidence is used internally for quality assurance and sign-off. It is never used for marketing without a separate approval by EvoHome Improvements.",
      ],
      route: "/portal/installer",
      routeLabel: "Open your jobs",
      helpSlug: "installer-evidence",
    },
    {
      id: "invoicing",
      title: "Invoicing",
      body: [
        "Once a job is eligible, submit your invoice from the job. Check your business details and logo are correct before submitting, then track its approval status.",
        "Your VAT position is a matter for your own business and is stated on your own invoice. It is separate from the VAT status of EvoHome Improvements Ltd.",
      ],
      helpSlug: "installer-invoicing",
    },
    {
      id: "completion",
      title: "Completing work correctly",
      body: [
        "To close a job properly, the agreed works must be finished, required evidence uploaded and any snags resolved. The job then moves through sign-off and invoice approval.",
      ],
      helpSlug: "installer-completion",
    },
    {
      id: "help",
      title: "Where to get help",
      body: [
        "Help and Tutorials is always available in your workspace navigation, and you may reopen this guide from there.",
        "For anything else, email office@evohomeimprovements.co.uk.",
      ],
      route: "/portal/installer/help",
      routeLabel: "Open Help and Tutorials",
    },
  ],
};

export const TOURS: Record<OnboardingAudience, OnboardingTour> = {
  homeowner: HOMEOWNER_TOUR,
  installer: INSTALLER_TOUR,
};

export function getTour(audience: OnboardingAudience): OnboardingTour {
  return TOURS[audience];
}
